import {
  captureSnapshots,
  compactSpeakerHistory,
  createPatches,
  getAudioKey,
  getProjectAudioCheckpoint,
  isAudioCurrentForProject,
} from '@src/services/history';
import { HISTORY_SCOPE } from '@src/constants/history';
import { CONVERTING_STATUS, PROJECT_PRODUCT } from '@src/constants/project';
import { actionTypes as historyActionTypes } from './actions';
import * as historyActions from './actions';
import * as projectActions from '../project/actions';
import { actionTypes as projectActionTypes } from '../project/actions';
import dubbingConfig from '../project/dubbingHistoryConfig';

const configByScope = {
  dubbing: dubbingConfig,
};

const PROJECT_AUDIO_ACTIONS = new Set([
  projectActionTypes.SET_AUDIO_LINK_SRT,
  projectActionTypes.SET_AUDIO_SRT_EXPIRED_AT,
]);

const isDeferredLabel = (config, label) =>
  Boolean(label) && (config?.deferLabels || []).includes(label);

// Auto-save bookkeeping that must NOT be part of undo/redo. `lastSaved` is the
// auto-save baseline; if history reverted it together with the content, an undo
// would move the baseline back in lockstep and the diff would look empty, so no
// save would fire. These fields are static when auto-save is off, so excluding
// them is a no-op there.
// Ephemeral playback/focus UI that is NOT document content. If history captured
// it, undo/redo would restore a "playing" flag AFTER pauseAudio() cleared it and
// re-trigger auto-play of a block (and flip focus/preview to a stale block).
const PLAYBACK_UI_KEYS = [
  'isPlaying',
  'isPlayingBlock',
  'isMultiBlockMode',
  'currentPlayingBlockId',
  'currentBlockId',
  'audioLink',
];

const HISTORY_IGNORED_KEYS = [
  'lastSaved',
  'autoSaveStatus',
  ...PLAYBACK_UI_KEYS,
];

const omitKeys = (obj, keys) =>
  Object.fromEntries(
    Object.entries(obj).filter(([key]) => !keys.includes(key)),
  );

// Per-block presentation flags that trigger a one-shot animation and must NOT
// be part of undo/redo. `showSuccessIcon` pulses the "just synthesised" check
// for 3s; if history captured it, an undo/redo that flips it back to true would
// replay the check on blocks the user isn't converting.
const TRANSIENT_BLOCK_KEYS = ['showSuccessIcon'];

const stripTransientBlockKeys = (project) => {
  const blocks = project?.multiBlock?.blocks;
  if (!blocks) return project;
  const nextBlocks = Object.fromEntries(
    Object.entries(blocks).map(([blockId, block]) => [
      blockId,
      omitKeys(block, TRANSIENT_BLOCK_KEYS),
    ]),
  );
  return {
    ...project,
    multiBlock: { ...project.multiBlock, blocks: nextBlocks },
  };
};

const diffProjectStates = (fromState, toState) =>
  createPatches(stripTransientBlockKeys(fromState), (draft) =>
    Object.assign(
      draft,
      omitKeys(stripTransientBlockKeys(toState), HISTORY_IGNORED_KEYS),
    ),
  );

// Builds the `*Before` and `*After` fields of a history entry from a pair
// of states. Flattens the bundled snapshots into the legacy field names
// that the project reducer reads via options.
const buildSnapshotFields = (fromState, toState) => {
  const before = captureSnapshots(fromState);
  const after = captureSnapshots(toState);
  return {
    blockPlayStateBefore: before.blockPlayState,
    blockPlayStateAfter: after.blockPlayState,
    originalBlocksBefore: before.originalBlocks,
    originalBlocksAfter: after.originalBlocks,
    projectAudioBefore: before.projectAudio,
    projectAudioAfter: after.projectAudio,
  };
};

// Build the options object that travels with HISTORY_APPLY_PATCHES so the
// project reducer can re-overlay block/originalBlocks/projectAudio from
// the entry's recorded snapshots. `direction` picks which side of the
// entry to read.
const buildApplyOptions = (entry, direction, audioPatchOptions) => {
  const suffix = direction === 'forward' ? 'After' : 'Before';
  return {
    ...audioPatchOptions,
    blockPlayStateSnapshot: entry[`blockPlayState${suffix}`] || {},
    originalBlocksSnapshot: entry[`originalBlocks${suffix}`] || {},
    projectAudioSnapshot: entry[`projectAudio${suffix}`] || null,
  };
};

const historyMiddleware = (store) => {
  const pendingByScope = {};
  const transactionByScope = {};

  const getProject = () => store.getState().project;
  const getHistorySlice = (scope) => store.getState().history?.[scope];

  const dispatch = (action) => store.dispatch(action);

  const applyPatches = (scope, patches, options) => {
    dispatch(historyActions.applyPatches(scope, patches, options));
  };

  const pauseAudio = () => {
    dispatch(projectActions.setIsPlaying(false));
    dispatch(projectActions.setIsPlayingBlock(false));
    dispatch(projectActions.setCurrentPlayingBlockId(''));
  };

  const buildEntry = (scope, label, fromState, toState) => {
    const { patches, inversePatches } = diffProjectStates(fromState, toState);
    return {
      label: label || 'change',
      scope,
      patches,
      inversePatches,
      audioKeyBefore: getAudioKey(fromState),
      audioKeyAfter: getAudioKey(toState),
      ...buildSnapshotFields(fromState, toState),
      timestamp: Date.now(),
    };
  };

  const shouldSyncLatestAudioCheckpoint = (action, project) =>
    PROJECT_AUDIO_ACTIONS.has(action.type) && isAudioCurrentForProject(project);

  const getAudioPatchOptions = (scope, targetAudioKey) => {
    const latestAudioCheckpoint = getHistorySlice(scope)?.latestAudioCheckpoint;
    return latestAudioCheckpoint?.audioKey === targetAudioKey
      ? { audioState: latestAudioCheckpoint }
      : { invalidateAudio: true };
  };

  const restoreProjectState = (scope, targetProject) => {
    const { patches } = diffProjectStates(getProject(), targetProject);
    const snapshots = captureSnapshots(targetProject);
    applyPatches(scope, patches, {
      ...getAudioPatchOptions(scope, getAudioKey(targetProject)),
      blockPlayStateSnapshot: snapshots.blockPlayState,
      originalBlocksSnapshot: snapshots.originalBlocks,
      projectAudioSnapshot: snapshots.projectAudio,
    });
  };

  const flushPending = (scope) => {
    const pending = pendingByScope[scope];
    if (!pending) return;
    const entry = buildEntry(
      scope,
      pending.label,
      pending.anchorBefore,
      getProject(),
    );
    delete pendingByScope[scope];
    dispatch(historyActions.commit(scope, entry));
  };

  const discardPending = (scope) => {
    const pending = pendingByScope[scope];
    if (!pending) return false;
    delete pendingByScope[scope];
    restoreProjectState(scope, pending.anchorBefore);
    return true;
  };

  const abortTransaction = (scope) => {
    const tx = transactionByScope[scope];
    if (!tx) return false;
    delete transactionByScope[scope];
    restoreProjectState(scope, tx.anchorBefore);
    return true;
  };

  // Synthesis updates (per-block audio, project SRT audio, originalBlocks
  // via SAVE_PROJECT) all land via untagged actions, so the head entry's
  // `*After` snapshots are stale by the time the audio actually arrives.
  // This refresh writes the current truth back onto the head entry — and
  // mirrors it into future[0]'s `*Before` so redo-then-undo returns to
  // the audio that was just generated.
  const refreshHeadAfterSynthesis = (scope) => {
    if (transactionByScope[scope]) return;
    flushPending(scope);
    dispatch(
      historyActions.refreshHeadSnapshots(
        scope,
        captureSnapshots(getProject()),
      ),
    );
  };

  const isDubbing = () => getProject().product === PROJECT_PRODUCT.DUBBING;

  const handleBlockSynthesisInfo = (action, next) => {
    const result = next(action);
    if (!isDubbing()) return result;
    const info = action.payload?.synthesisInfo || {};
    const { status } = info;
    if (status === CONVERTING_STATUS.IN_PROGRESS) return result;
    // Refresh on a settled synthesis OR a timeline realign (endTime shrunk to
    // the audio length arrives as a status-less update in the socket flow) —
    // both must be written into the head entry so redo keeps the new time.
    const hasTimelineChange = 'startTime' in info || 'endTime' in info;
    if (!status && !hasTimelineChange) return result;
    refreshHeadAfterSynthesis(HISTORY_SCOPE.DUBBING);
    return result;
  };

  const handleMultiBlockSynthesisInfo = (action, next) => {
    const result = next(action);
    if (!isDubbing()) return result;
    const { blockIds = [], synthesisInfos = [] } = action.payload || {};
    const anyCompleted = blockIds.some((blockId) => {
      const info = synthesisInfos.find((i) => i.id === blockId);
      return info && info.status !== CONVERTING_STATUS.IN_PROGRESS;
    });
    if (anyCompleted) refreshHeadAfterSynthesis(HISTORY_SCOPE.DUBBING);
    return result;
  };

  // Fired by the video create-audio flow: `updateMultiBlockSynthesisStatus`
  // carries the freshly arrived audioLink AND the endTime realigned to that
  // audio's length. Both land after the edit's head entry was committed, so
  // refresh the head's `*After` snapshot to the current truth — otherwise a
  // redo would restore the pre-adjust endTime. Skip while every block is still
  // IN_PROGRESS (no settled change worth capturing yet).
  const handleMultiBlockSynthesisStatus = (action, next) => {
    const result = next(action);
    if (!isDubbing()) return result;
    const updates = Object.values(action.payload?.blocks || {});
    const allInProgress =
      updates.length > 0 &&
      updates.every((u) => u?.status === CONVERTING_STATUS.IN_PROGRESS);
    if (allInProgress) return result;
    refreshHeadAfterSynthesis(HISTORY_SCOPE.DUBBING);
    return result;
  };

  const handleAudioStateUpdate = (action, next) => {
    const result = next(action);
    const project = getProject();
    if (!shouldSyncLatestAudioCheckpoint(action, project)) return result;
    if (transactionByScope[HISTORY_SCOPE.DUBBING]) return result;
    // Keep `latestAudioCheckpoint` in step with the latest synth, but
    // don't reset future: per-entry `projectAudio*` snapshots already let
    // redo walk back into future steps after audio changes here.
    dispatch(
      historyActions.setAudioCheckpoint(
        HISTORY_SCOPE.DUBBING,
        getProjectAudioCheckpoint(project),
        { resetFuture: false },
      ),
    );
    refreshHeadAfterSynthesis(HISTORY_SCOPE.DUBBING);
    return result;
  };

  const handleDeleteSpeaker = (action, next) => {
    const scope = HISTORY_SCOPE.DUBBING;
    const deletedId = action.payload;

    abortTransaction(scope);
    flushPending(scope);

    const result = next(action);

    const slice = getHistorySlice(scope);
    if (slice) {
      const compacted = compactSpeakerHistory(
        slice,
        getProject(),
        deletedId,
        scope,
      );
      if (compacted !== slice) {
        dispatch(historyActions.purgeReference(scope, compacted));
      }
    }

    if (getProject().currentFilteringSpeakerId === deletedId) {
      dispatch(projectActions.setFilterSpeakerId(null));
    }

    return result;
  };

  const handleTransactionBegin = (action, next) => {
    const { scope, label } = action.payload;
    if (transactionByScope[scope]) return next(action);
    flushPending(scope);
    transactionByScope[scope] = { anchorBefore: getProject(), label };
    return next(action);
  };

  const handleTransactionEnd = (action, next) => {
    const { scope } = action.payload;
    const tx = transactionByScope[scope];
    if (!tx) return next(action);
    const result = next(action);
    const entry = buildEntry(scope, tx.label, tx.anchorBefore, getProject());
    delete transactionByScope[scope];
    if (entry.patches.length > 0) {
      dispatch(historyActions.commit(scope, entry));
    }
    return result;
  };

  const handleUndoRedo = (action, next, direction) => {
    const { scope } = action.payload;
    pauseAudio();
    if (abortTransaction(scope)) return undefined;
    if (discardPending(scope)) return undefined;
    const slice = getHistorySlice(scope);
    const stack = direction === 'forward' ? slice?.future : slice?.past;
    if (!stack || stack.length === 0) return next(action);
    const entry = direction === 'forward' ? stack[0] : stack[stack.length - 1];
    const result = next(action);
    const targetAudioKey =
      direction === 'forward' ? entry.audioKeyAfter : entry.audioKeyBefore;
    const patches =
      direction === 'forward' ? entry.patches : entry.inversePatches;
    applyPatches(
      scope,
      patches,
      buildApplyOptions(
        entry,
        direction,
        getAudioPatchOptions(scope, targetAudioKey),
      ),
    );
    return result;
  };

  const handleHistoryCommit = (action, next, historyMeta) => {
    const { scope, label } = historyMeta;
    const config = configByScope[scope];
    if (!config) return next(action);
    if (transactionByScope[scope]) return next(action);

    const beforeProject = getProject();
    const result = next(action);

    if (isDeferredLabel(config, label)) {
      if (!pendingByScope[scope]) {
        pendingByScope[scope] = { anchorBefore: beforeProject, label };
      }
      return result;
    }

    flushPending(scope);
    const entry = buildEntry(scope, label, beforeProject, getProject());
    dispatch(historyActions.commit(scope, entry));
    return result;
  };

  // Action-type → handler routing table. Keeps the main middleware loop
  // flat instead of a long if/else chain.
  const handlers = {
    [projectActionTypes.UPDATE_BLOCK_SYNTHESIS_INFO]: handleBlockSynthesisInfo,
    [projectActionTypes.UPDATE_MULTI_BLOCK_SYNTHESIS_INFO]:
      handleMultiBlockSynthesisInfo,
    [projectActionTypes.UPDATE_MULTI_BLOCK_SYNTHESIS_STATUS]:
      handleMultiBlockSynthesisStatus,
    [projectActionTypes.DELETE_SPEAKER]: handleDeleteSpeaker,
    [historyActionTypes.HISTORY_TRANSACTION_BEGIN]: handleTransactionBegin,
    [historyActionTypes.HISTORY_TRANSACTION_END]: handleTransactionEnd,
    [historyActionTypes.HISTORY_UNDO]: (action, next) =>
      handleUndoRedo(action, next, 'backward'),
    [historyActionTypes.HISTORY_REDO]: (action, next) =>
      handleUndoRedo(action, next, 'forward'),
    [historyActionTypes.HISTORY_FLUSH]: (action, next) => {
      const { scope } = action.payload;
      if (!transactionByScope[scope]) flushPending(scope);
      return next(action);
    },
    [historyActionTypes.HISTORY_CLEAR]: (action, next) => {
      const { scope } = action.payload;
      delete pendingByScope[scope];
      delete transactionByScope[scope];
      return next(action);
    },
  };

  return (next) => (action) => {
    if (PROJECT_AUDIO_ACTIONS.has(action?.type)) {
      return handleAudioStateUpdate(action, next);
    }
    const handler = handlers[action?.type];
    if (handler) return handler(action, next);

    const historyMeta = action?.meta?.history;
    if (historyMeta?.commit)
      return handleHistoryCommit(action, next, historyMeta);

    return next(action);
  };
};

export default historyMiddleware;
